import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { FileUploadService, FileUploadItem, FileUploadStatus } from '@core/services/file-upload.service';
import { formatFileBytes, getFileKind } from '@shared/utility/file-asset.util';
import { TranslatePipe } from '@shared/pipes/translate.pipe';

/**
 * Global, signal-driven upload tray for the Dateiablage. Mirrors the central
 * {@link FileUploadService} queue so progress, cancel, retry and dismiss are
 * available from anywhere in the desktop shell. Mobile surfaces render their
 * own inline progress banner, so this tray is desktop-only.
 */
@Component({
  selector: 'app-file-upload-tray',
  standalone: true,
  imports: [CommonModule, NzButtonModule, NzIconModule, TranslatePipe],
  templateUrl: './file-upload-tray.component.html',
  styleUrls: ['./file-upload-tray.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadTrayComponent {
  private readonly fileUploadService = inject(FileUploadService);

  readonly items = this.fileUploadService.items;
  readonly hasActive = this.fileUploadService.hasActive;
  readonly totalProgress = this.fileUploadService.totalProgressPercent;

  readonly hasFinished = computed(() =>
    this.items().some(
      (item) => item.status === 'done' || item.status === 'error' || item.status === 'canceled',
    ),
  );
  readonly visible = computed(() => this.items().length > 0);

  isActive(status: FileUploadStatus): boolean {
    return status === 'queued' || status === 'uploading' || status === 'finalizing';
  }

  trackById(_index: number, item: FileUploadItem): string {
    return item.id;
  }

  fileSize(bytes: number): string {
    return formatFileBytes(bytes);
  }

  fileKindIcon(item: FileUploadItem): string {
    return getFileKind(item.fileName, item.mimeType).nzIcon;
  }

  statusIcon(status: FileUploadStatus): string {
    switch (status) {
      case 'done':
        return 'check-circle';
      case 'error':
        return 'close-circle';
      case 'canceled':
        return 'minus-circle';
      default:
        return 'clock-circle';
    }
  }

  statusKey(status: FileUploadStatus): string {
    return `fileUpload.tray.${status}`;
  }

  statusTone(status: FileUploadStatus): string {
    switch (status) {
      case 'done':
        return 'constructive';
      case 'error':
        return 'destructive';
      case 'canceled':
        return 'muted';
      default:
        return 'info';
    }
  }

  cancel(id: string): void {
    this.fileUploadService.cancel(id);
  }

  retry(id: string): void {
    this.fileUploadService.retry(id);
  }

  dismiss(id: string): void {
    this.fileUploadService.dismiss(id);
  }

  clearFinished(): void {
    this.fileUploadService.clearFinished();
  }
}
